/**
 * "WORK LENS", AND DELIBERATELY NOT "ROLE".
 *
 * Handoff 02 closed a defect whose whole shape was a control called "Role"
 * that changed no role. This is the same hazard from the other direction: a
 * control that changes what you SEE, sitting on a page full of roles, one bad
 * label away from reading as a control that changes what you ARE.
 *
 * So the label is "Work lens", the helper line says what switching does, and
 * every option carries its provenance — Current role / Historical role — so a
 * person whose Detail Specialist role was removed last month can tell that the
 * evidence is still theirs and the role is not.
 *
 * NOTHING HERE WRITES. There is no mutation, no RPC and no `supabase` import.
 * Selecting a lens changes component state and nothing else, which is the
 * property `workLensSwitcherIsPresentationOnly` in the test suite pins by
 * scanning this file's source.
 */

import { Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  PROVENANCE_LABEL,
  lensProvenance,
  type PassportWorkLens,
  type PassportWorkLensResolution,
} from '@/lib/passport/workLens';

export interface PassportWorkLensSwitcherProps {
  resolution: PassportWorkLensResolution;
  selected: PassportWorkLens | null;
  onSelect: (lens: PassportWorkLens) => void;
  /** The evidence read failed; say so instead of implying there is none. */
  evidenceReadFailed?: boolean;
}

export function PassportWorkLensSwitcher({
  resolution,
  selected,
  onSelect,
  evidenceReadFailed,
}: PassportWorkLensSwitcherProps) {
  // Still loading one of the two halves. Drawing a partial list would read as
  // a role appearing, so nothing is drawn at all.
  if (resolution.resolving) return null;

  if (resolution.lenses.length === 0) {
    // A real state, and one worth naming: somebody with no operational work
    // roles and no recorded work has identity evidence and no work evidence.
    // Silence here would look like a section that failed to load.
    return (
      <div
        className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground"
        data-testid="work-lens-none"
      >
        {evidenceReadFailed
          ? 'Work history could not be loaded, so no work lenses are shown.'
          : resolution.currentWorkRolesStated
            ? 'No shop work roles and no recorded work yet — this Passport shows your identity and application record.'
            : 'No work roles have been set for you yet, and no work has been recorded.'}
      </div>
    );
  }

  const onlyOne = resolution.lenses.length === 1;

  return (
    <div
      className="flex flex-col gap-2 rounded-md border bg-muted/30 px-3 py-2"
      data-testid="work-lens-switcher"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Eye className="h-3.5 w-3.5" />
          Work lens
        </span>
        {resolution.lenses.map((lens) => {
          const isSelected = lens.role === selected;
          return (
            <Button
              key={lens.role}
              type="button"
              size="sm"
              variant={isSelected ? 'default' : 'outline'}
              className="h-7 gap-1.5 text-xs"
              aria-pressed={isSelected}
              data-testid={`work-lens-option-${lens.role}`}
              onClick={() => onSelect(lens.role)}
            >
              {lens.label}
              <Badge
                variant="secondary"
                className="px-1 py-0 text-[10px] font-normal"
              >
                {PROVENANCE_LABEL[lensProvenance(lens)]}
              </Badge>
            </Button>
          );
        })}
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        {onlyOne
          ? 'Viewing the evidence recorded for this work. Changing the lens only changes what is shown — it does not change your job, your roles or your access.'
          : 'Choose which work to view evidence for. Changing the lens only changes what is shown — it does not change your job, your roles or your access.'}
        {resolution.lenses.some((l) => !l.current) && (
          <>
            {' '}
            <span data-testid="work-lens-historical-help">
              A historical role is work you have done and no longer hold. The
              record of it stays.
            </span>
          </>
        )}
        {resolution.hasUnattributedHistory && (
          <>
            {' '}
            <span data-testid="work-lens-unattributed-help">
              Some of your earlier work was recorded before work roles existed,
              so it is not filed under any lens.
            </span>
          </>
        )}
        {resolution.nonLensWorkRoles.length > 0 && (
          <>
            {' '}
            <span data-testid="work-lens-nonlens-help">
              You also hold{' '}
              {resolution.nonLensWorkRoles
                .map((r) => (r === 'DISPATCHER' ? 'Dispatcher' : 'Parts'))
                .join(' and ')}
              . No work evidence is recorded for that yet.
            </span>
          </>
        )}
      </p>
    </div>
  );
}
